/**
 * Format quote update request changes for Ops-readable display.
 * Converts raw { from, to } payloads into human labels and side-by-side values.
 */

const FIELD_LABELS = {
  from: "Pickup location",
  to: "Delivery location",
  address: "Address",
  dates: "Move date(s)",
  date: "Move date",
  items: "Inventory",
  notes: "Customer notes",
  name: "Customer name",
  email: "Email",
  phone: "Phone",
  pickupAddress: "Pickup address",
  deliveryAddress: "Delivery address",
};

/**
 * @param {string} key
 * @returns {string}
 */
export function getChangeFieldLabel(key) {
  if (!key) return "Unknown field";
  return FIELD_LABELS[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function formatDateValue(value) {
  if (isEmpty(value)) return null;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return null;
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw);
    return date.toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(raw);
  }
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function formatDatesList(value) {
  if (isEmpty(value)) return null;
  if (!Array.isArray(value)) return formatDateValue(value);
  const formatted = value.map((d) => formatDateValue(d)).filter(Boolean);
  return formatted.length > 0 ? formatted.join(", ") : null;
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function formatAddress(value) {
  if (isEmpty(value)) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "object") {
    const address =
      value.address ||
      value.formattedAddress ||
      value.formatted_address ||
      [value.street, value.suburb, value.city, value.postalCode || value.postal_code]
        .filter(Boolean)
        .join(", ");
    return address?.trim() || null;
  }
  return String(value);
}

/**
 * Normalize an inventory item into { key, label, quantity }.
 * @param {unknown} item
 * @param {number} index
 * @returns {{ key: string, label: string, quantity: number }}
 */
function normalizeItem(item, index = 0) {
  if (item == null) {
    return { key: `empty-${index}`, label: "Item", quantity: 1 };
  }
  if (typeof item === "string") {
    const trimmed = item.trim() || "Item";
    return { key: trimmed.toLowerCase(), label: trimmed, quantity: 1 };
  }
  if (typeof item !== "object") {
    const label = String(item);
    return { key: label.toLowerCase(), label, quantity: 1 };
  }

  const label =
    item.description ||
    item.content ||
    item.caption ||
    item.filename ||
    item.name ||
    "Item";
  const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
  const key = String(label).trim().toLowerCase();
  return { key, label: String(label).trim() || "Item", quantity };
}

/**
 * @param {unknown} items
 * @returns {string|null}
 */
export function formatItemsSummary(items) {
  if (isEmpty(items)) return null;
  if (!Array.isArray(items)) return String(items);
  if (items.length === 0) return null;

  return items
    .map((item, i) => {
      const { label, quantity } = normalizeItem(item, i);
      return quantity > 1 ? `${quantity} × ${label}` : label;
    })
    .join("; ");
}

/**
 * Diff inventory lists into added / removed / quantity-changed rows.
 * @param {unknown} fromItems
 * @param {unknown} toItems
 * @returns {Array<{ type: 'added'|'removed'|'changed', label: string, fromQty?: number, toQty?: number }>}
 */
export function diffInventoryItems(fromItems, toItems) {
  const fromList = Array.isArray(fromItems) ? fromItems : [];
  const toList = Array.isArray(toItems) ? toItems : [];

  const fromMap = new Map();
  fromList.forEach((item, i) => {
    const n = normalizeItem(item, i);
    const existing = fromMap.get(n.key);
    if (existing) {
      existing.quantity += n.quantity;
    } else {
      fromMap.set(n.key, { label: n.label, quantity: n.quantity });
    }
  });

  const toMap = new Map();
  toList.forEach((item, i) => {
    const n = normalizeItem(item, i);
    const existing = toMap.get(n.key);
    if (existing) {
      existing.quantity += n.quantity;
    } else {
      toMap.set(n.key, { label: n.label, quantity: n.quantity });
    }
  });

  const rows = [];
  const allKeys = new Set([...fromMap.keys(), ...toMap.keys()]);

  for (const key of allKeys) {
    const from = fromMap.get(key);
    const to = toMap.get(key);

    if (!from && to) {
      rows.push({ type: "added", label: to.label, toQty: to.quantity });
    } else if (from && !to) {
      rows.push({ type: "removed", label: from.label, fromQty: from.quantity });
    } else if (from && to && from.quantity !== to.quantity) {
      rows.push({
        type: "changed",
        label: from.label,
        fromQty: from.quantity,
        toQty: to.quantity,
      });
    }
  }

  return rows;
}

/**
 * Format a single field value for display.
 * @param {string} key
 * @param {unknown} value
 * @returns {string}
 */
export function formatChangeValue(key, value) {
  if (isEmpty(value)) return "Not provided";

  switch (key) {
    case "dates":
      return formatDatesList(value) || "Not provided";
    case "date":
      return formatDateValue(value) || "Not provided";
    case "pickupAddress":
    case "deliveryAddress":
    case "address":
      return formatAddress(value) || "Not provided";
    case "items":
      return formatItemsSummary(value) || "Not provided";
    case "from":
    case "to":
      if (typeof value === "object") {
        return formatAddress(value) || "Not provided";
      }
      return String(value).trim() || "Not provided";
    default:
      if (Array.isArray(value)) {
        return value.map((v) => (typeof v === "object" ? formatAddress(v) || JSON.stringify(v) : String(v))).join(", ");
      }
      if (typeof value === "object") {
        return formatAddress(value) || "Updated details";
      }
      return String(value);
  }
}

/**
 * Relative time for submittedAt (e.g. "12 minutes ago").
 * @param {string|Date|null|undefined} submittedAt
 * @returns {string|null}
 */
export function formatRelativeSubmittedAt(submittedAt) {
  if (!submittedAt) return null;
  try {
    const date = new Date(submittedAt);
    if (Number.isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return "just now";

    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } catch {
    return null;
  }
}

/**
 * Build display rows from request.changes.
 * @param {Object|null|undefined} changes
 * @returns {Array<{ key: string, label: string, fromDisplay: string, toDisplay: string, itemDiff: Array|null }>}
 */
export function buildChangeDisplayRows(changes) {
  if (!changes || typeof changes !== "object") return [];

  return Object.entries(changes).map(([key, value]) => {
    const from = value?.from;
    const to = value?.to;
    const itemDiff = key === "items" ? diffInventoryItems(from, to) : null;

    return {
      key,
      label: getChangeFieldLabel(key),
      fromDisplay: formatChangeValue(key, from),
      toDisplay: formatChangeValue(key, to),
      itemDiff: itemDiff && itemDiff.length > 0 ? itemDiff : null,
    };
  });
}
